import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Briefcase, GraduationCap, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const Careers = () => {
  return (
    <div className="bg-cream text-ink">
      <Helmet>
        <title>Careers | Join Valentis Law Group</title>
        <meta name="description" content="Build your legal career at Valentis Law Group. Explore opportunities with a leading cross-border dispute resolution and arbitration practice." />
        <link rel="canonical" href="https://valentislawgroup.com/careers" />
        <meta property="og:title" content="Careers | Join Valentis Law Group" />
        <meta property="og:description" content="Build your legal career at Valentis Law Group. Explore opportunities with a leading cross-border dispute resolution and arbitration practice." />
        <meta property="og:url" content="https://valentislawgroup.com/careers" />
        <meta name="twitter:title" content="Careers | Join Valentis Law Group" />
        <meta name="twitter:description" content="Build your legal career at Valentis Law Group. Explore opportunities with a leading cross-border dispute resolution and arbitration practice." />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-40 pb-24 lg:pt-48 lg:pb-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl">
            <p className="text-sm uppercase tracking-widest text-ink/60 mb-6">Careers</p>
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-wide leading-tight mb-8">
              Build Your Career with VALENTIS
            </h1>
            <p className="text-xl text-ink/70 leading-relaxed">
              Join a team of exceptional attorneys committed to excellence, innovation, and professional growth in an environment that values both individual achievement and collaborative success.
            </p>
          </div>
        </div>
      </section>

      {/* Why Join Us */}
      <section className="py-24 lg:py-32 border-t border-ink/10 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-wide mb-6">Why VALENTIS?</h2>
            <p className="text-lg text-ink/70 max-w-3xl mx-auto">
              We offer more than just a position—we provide a platform for exceptional legal minds to thrive and make their mark.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'World-Class Work',
                description: 'Engage with sophisticated legal matters for prestigious clients across diverse industries and jurisdictions.'
              },
              {
                title: 'Professional Development',
                description: 'Comprehensive training programs, mentorship opportunities, and clear paths to partnership.'
              },
              {
                title: 'Collaborative Culture',
                description: 'Work alongside industry leaders in an environment that values innovation, diversity, and mutual respect.'
              },
              {
                title: 'Competitive Compensation',
                description: 'Market-leading salaries, performance bonuses, and comprehensive benefits packages.'
              }
            ].map((benefit, index) => (
              <div key={index} className="bg-cream border border-ink/10 p-8 hover:border-ink/30 transition-all duration-500 group">
                <h3 className="font-serif text-2xl mb-4 group-hover:text-ink/90 transition-colors">{benefit.title}</h3>
                <p className="text-ink/60 text-sm leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">Join Our Team</h2>
            <p className="text-xl text-ink/70 mb-8 leading-relaxed">
              We are always looking for talented legal professionals to join VALENTIS LAW GROUP. If you are interested in career opportunities with our firm, please reach out to us.
            </p>
            <div className="bg-ink/5 backdrop-blur-sm border border-ink/10 p-12 rounded-lg">
              <svg className="w-16 h-16 text-ink/60 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <h3 className="font-serif text-3xl mb-4">Get in Touch</h3>
              <p className="text-lg text-ink/70 mb-6">
                For career inquiries and opportunities, please contact us at:
              </p>
              <a 
                href="mailto:contact@valentislaw.com"
                className="inline-flex items-center text-2xl md:text-3xl font-medium text-ink hover:text-ink/80 transition-colors duration-300"
              >
                contact@valentislaw.com
                <ArrowRight className="ml-3" size={28} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-6">What We Look For</h2>
            <p className="text-lg text-ink/70 max-w-3xl mx-auto">
              We seek exceptional candidates who embody our values and demonstrate a commitment to excellence.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: GraduationCap,
                title: 'Academic Excellence',
                description: 'Top-tier law school credentials with outstanding academic performance and relevant experience.'
              },
              {
                icon: Briefcase,
                title: 'Professional Excellence',
                description: 'Demonstrated expertise in complex legal matters and a track record of delivering exceptional results.'
              },
              {
                icon: ArrowRight,
                title: 'Growth Mindset',
                description: 'Commitment to continuous learning, professional development, and collaborative team success.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-cream border border-ink/10 p-10 text-center hover:border-ink/30 transition-all duration-500 group">
                <item.icon size={40} className="mx-auto mb-6 group-hover:scale-110 transition-transform duration-300" />
                <h3 className="font-serif text-2xl mb-4">{item.title}</h3>
                <p className="text-ink/60 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application Process */}
      <section className="py-24 lg:py-32 border-t border-ink/10">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-6">Our Hiring Process</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              { step: '01', title: 'Application Review', desc: 'Submit your resume and cover letter' },
              { step: '02', title: 'Initial Interview', desc: 'Phone or video conversation' },
              { step: '03', title: 'Office Interview', desc: 'Meet our team in person' },
              { step: '04', title: 'Offer', desc: 'Join VALENTIS LAW GROUP' }
            ].map((item, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="font-serif text-5xl text-ink/20">{item.step}</div>
                <h3 className="font-serif text-xl">{item.title}</h3>
                <p className="text-sm text-ink/60">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 lg:py-32 bg-sand-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">
            Ready to Join Us?
          </h2>
          <p className="text-xl text-ink/70 mb-12">
            We look forward to hearing from exceptional legal professionals who want to be part of our team.
          </p>
          <Button
            asChild
            size="lg"
            className="bg-ink text-cream hover:bg-ink/90 px-8 py-6 text-base font-medium tracking-wider transition-all duration-300 hover:scale-105"
          >
            <a href="mailto:contact@valentislaw.com">Email Us Your Resume</a>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Careers;